import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Inject, Input, HostBinding, ElementRef, Directive, ViewChild, NgModule } from '@angular/core';
import * as i1$2 from '@taiga-ui/cdk';
import { tuiIsEdgeOlderThan, CHROMIUM_EDGE_START_VERSION, TuiResizeService, TuiDestroyService, tuiPure, tuiIsString, TuiRepeatTimesModule } from '@taiga-ui/cdk';
import { TUI_MODE, MODE_PROVIDER } from '@taiga-ui/core';
import * as i1 from 'rxjs';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { __decorate } from 'tslib';
import { USER_AGENT, WINDOW } from '@ng-web-apis/common';
import { map, distinctUntilChanged, delay } from 'rxjs/operators';

class TuiProgressBarComponent {
    constructor(mode$) {
        this.mode$ = mode$;
        this.size = 'm';
    }
}
TuiProgressBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressBarComponent, deps: [{ token: TUI_MODE }], target: i0.ɵɵFactoryTarget.Component });
TuiProgressBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiProgressBarComponent, selector: "progress[tuiProgressBar]", inputs: { color: "color", size: "size" }, host: { listeners: { "$.data-mode.attr": "mode$" }, properties: { "style.--tui-progress-color": "this.color", "attr.data-size": "this.size" } }, providers: [MODE_PROVIDER], ngImport: i0, template: '', isInline: true, styles: ["@keyframes tuiIndeterminateAnimation{50%{background-position:left}}:host{-webkit-appearance:none;-moz-appearance:none;appearance:none;border:none;display:block;width:100%;height:var(--t-height);color:var(--tui-primary);background:var(--tui-clear);-webkit-clip-path:inset(0 round var(--tui-radius-xs));clip-path:inset(0 round var(--tui-radius-xs));overflow:hidden}:host::-webkit-progress-value{-webkit-transition:width var(--tui-duration) linear;transition:width var(--tui-duration) linear}:host::-webkit-progress-value{background:var(--tui-progress-color, currentColor)}:host::-moz-progress-bar{background:var(--tui-progress-color, currentColor)}:host[data-mode=onDark]{--tui-clear: var(--tui-clear-inverse)}:host[data-size=m]{--t-height: 1.5rem}:host[data-size=s]{--t-height: .5rem}:host[data-size=s]::-webkit-progress-value{border-radius:var(--tui-radius-xs)}:host[data-size=s]::-moz-progress-bar{border-radius:var(--tui-radius-xs)}:host[new]{-webkit-clip-path:inset(0 .5px round var(--tui-radius-m));clip-path:inset(0 .5px round var(--tui-radius-m));border-radius:1rem}:host[new]::-webkit-progress-value{border-radius:inherit}:host[new]::-moz-progress-bar{border-radius:inherit}:host[new][data-size=xs]{--t-height: .25rem}:host[new][data-size=s]{--t-height: .5rem}:host[new][data-size=m]{--t-height: .75rem}:host[new][data-size=l]{--t-height: 1rem}:host[new][data-size=xl]{--t-height: 1.25rem}:host[new][data-size=xxl]{--t-height: 1.5rem}:host:indeterminate{background:linear-gradient(to right,var(--tui-clear) 0 45%,var(--tui-progress-color, currentColor) 45% 55%,var(--tui-clear) 55% 100%) right;background-size:225%;animation:tuiIndeterminateAnimation 3s infinite ease-in-out}:host:indeterminate::-webkit-progress-value{background:transparent}:host:indeterminate::-moz-progress-bar{background:transparent}:host::-webkit-progress-inner-element{border-radius:inherit}:host::-webkit-progress-bar{background:transparent;border-radius:inherit}:host-context(label[tuiProgressLabel]):not(:first-child){position:absolute;top:0;left:0;width:100%;height:100%;background:transparent}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'progress[tuiProgressBar]',
                    template: '',
                    styleUrls: ['./progress-bar.component.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [MODE_PROVIDER],
                    host: {
                        '($.data-mode.attr)': 'mode$',
                    },
                }]
        }], ctorParameters: function () { return [{ type: i1.Observable, decorators: [{
                    type: Inject,
                    args: [TUI_MODE]
                }] }]; }, propDecorators: { color: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.--tui-progress-color']
            }], size: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.data-size']
            }] } });

function calculateColorSegments(colors, progressWidth) {
    const segmentWidth = Math.ceil(progressWidth / colors.length);
    const colorsString = colors.reduce((acc, color, i) => `${acc}, ${color} ${i * segmentWidth}px ${(i + 1) * segmentWidth}px`, '');
    return `linear-gradient(to right ${colorsString})`;
}
class TuiProgressColorSegmentsDirective {
    constructor(el, resize$, userAgent) {
        this.el = el;
        this.resize$ = resize$;
        this.userAgent = userAgent;
        // TODO: drop support of legacy Edge (EdgeHTML) in v4.x
        this.isOldBrowsers = tuiIsEdgeOlderThan(CHROMIUM_EDGE_START_VERSION, this.userAgent);
        this.colors$ = new BehaviorSubject([]);
    }
    set colors(colors) {
        this.colors$.next(colors);
    }
    get calcSegments$() {
        return combineLatest([
            this.colors$,
            this.resize$.pipe(map(() => this.el.nativeElement.offsetWidth), distinctUntilChanged()),
        ]).pipe(map(([colors, width]) => this.isOldBrowsers ? colors[0] : calculateColorSegments(colors, width)));
    }
}
TuiProgressColorSegmentsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressColorSegmentsDirective, deps: [{ token: ElementRef }, { token: TuiResizeService }, { token: USER_AGENT }], target: i0.ɵɵFactoryTarget.Directive });
TuiProgressColorSegmentsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: TuiProgressColorSegmentsDirective, selector: "progress[tuiProgressBar][tuiProgressColorSegments]", inputs: { colors: ["tuiProgressColorSegments", "colors"] }, host: { listeners: { "$.style.--tui-progress-color": "0" }, properties: { "$.style.--tui-progress-color": "calcSegments$" } }, providers: [TuiDestroyService, TuiResizeService], ngImport: i0 });
__decorate([
    tuiPure
], TuiProgressColorSegmentsDirective.prototype, "calcSegments$", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressColorSegmentsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'progress[tuiProgressBar][tuiProgressColorSegments]',
                    providers: [TuiDestroyService, TuiResizeService],
                    host: {
                        '[$.style.--tui-progress-color]': 'calcSegments$',
                        '($.style.--tui-progress-color)': '0',
                    },
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [ElementRef]
                }] }, { type: i1.Observable, decorators: [{
                    type: Inject,
                    args: [TuiResizeService]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [USER_AGENT]
                }] }]; }, propDecorators: { colors: [{
                type: Input,
                args: ['tuiProgressColorSegments']
            }], calcSegments$: [] } });

class TuiProgressCircleComponent {
    constructor(userAgent, win, el, mode$) {
        this.userAgent = userAgent;
        this.win = win;
        this.el = el;
        this.mode$ = mode$;
        this.value = 0;
        this.max = 1;
        this.color = null;
        this.size = 'm';
        this.animationDelay$ = of(true).pipe(delay(0));
    }
    get progressRatio() {
        const ratio = this.value / this.max;
        return Number.isFinite(ratio) ? ratio : 0;
    }
    // TODO: drop support of legacy Edge (EdgeHTML) in v4.x
    get oldEdgeRadiusFallback() {
        if (!tuiIsEdgeOlderThan(CHROMIUM_EDGE_START_VERSION, this.userAgent)) {
            return null;
        }
        const strokeWidth = parseInt(this.win.getComputedStyle(this.progressCircle.nativeElement).strokeWidth, 10);
        return (this.el.nativeElement.offsetWidth - strokeWidth) / 2;
    }
}
TuiProgressCircleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressCircleComponent, deps: [{ token: USER_AGENT }, { token: WINDOW }, { token: ElementRef }, { token: TUI_MODE }], target: i0.ɵɵFactoryTarget.Component });
TuiProgressCircleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiProgressCircleComponent, selector: "tui-progress-circle", inputs: { value: "value", max: "max", color: "color", size: "size" }, host: { listeners: { "$.data-mode.attr": "mode$" }, properties: { "style.--tui-progress-color": "this.color", "attr.data-size": "this.size", "style.--progress-ratio": "this.progressRatio" } }, providers: [MODE_PROVIDER], viewQueries: [{ propertyName: "progressCircle", first: true, predicate: ["progressCircle"], descendants: true, static: true }], ngImport: i0, template: "<progress\n    class=\"t-hidden-progress\"\n    [max]=\"max\"\n    [value]=\"value\"\n></progress>\n\n<svg\n    aria-hidden=\"true\"\n    height=\"100%\"\n    width=\"100%\"\n    class=\"t-svg\"\n>\n    <circle\n        cx=\"50%\"\n        cy=\"50%\"\n        class=\"t-track\"\n        [attr.r]=\"oldEdgeRadiusFallback\"\n    ></circle>\n\n    <circle\n        #progressCircle\n        cx=\"50%\"\n        cy=\"50%\"\n        class=\"t-progress\"\n        [attr.r]=\"oldEdgeRadiusFallback\"\n        [class.t-progress_filled]=\"animationDelay$ | async\"\n    ></circle>\n</svg>\n", styles: [":host{position:relative;display:block;color:var(--tui-primary);transform:rotate(-90deg);transform-origin:center;font-size:1rem;width:var(--t-diameter);height:var(--t-diameter)}:host[data-mode=onDark]{--tui-clear: var(--tui-clear-inverse)}:host[data-size=s]{--t-diameter: 2em;--t-track-stroke: .5em;--t-progress-stroke: .5em}:host[data-size=m]{--t-diameter: 3em;--t-track-stroke: .375em;--t-progress-stroke: .375em}:host[data-size=l]{--t-diameter: 4em;--t-track-stroke: .375em;--t-progress-stroke: .375em}:host[data-size=xl]{--t-diameter: 7em;--t-track-stroke: .375em;--t-progress-stroke: .375em}:host[new]{--t-track-stroke: var(--tui-thickness, .375em);--t-progress-stroke: var(--tui-thickness, .375em)}:host[new][data-size=xxs]{--t-diameter: 2rem}:host[new][data-size=xs]{--t-diameter: 2.5rem}:host[new][data-size=s]{--t-diameter: 3.5rem}:host[new][data-size=m]{--t-diameter: 4rem}:host[new][data-size=l]{--t-diameter: 5rem}:host[new][data-size=xl]{--t-diameter: 6rem}:host[new][data-size=xxl]{--t-diameter: 8rem}.t-track{fill:transparent;stroke:var(--tui-clear);stroke-width:var(--t-track-stroke);r:calc((var(--t-diameter) - var(--t-track-stroke)) / 2)}.t-progress{fill:transparent;stroke:var(--tui-progress-color, currentColor);stroke-linecap:round;stroke-width:var(--t-progress-stroke);stroke-dasharray:calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2));stroke-dashoffset:calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2));r:calc((var(--t-diameter) - var(--t-progress-stroke)) / 2)}.t-progress_filled{transition:stroke-dashoffset var(--tui-duration) linear;stroke-dashoffset:calc(calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2)) - var(--progress-ratio) * calc(2 * 3.14159265 * calc((var(--t-diameter) - var(--t-progress-stroke)) / 2)))}.t-hidden-progress{position:absolute;clip:rect(1px,1px,1px,1px);-webkit-clip-path:inset(50%);clip-path:inset(50%);height:1px;width:1px;margin:-1px;overflow:hidden;padding:0}.t-svg{overflow:unset}\n"], pipes: { "async": i1$1.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressCircleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-progress-circle',
                    templateUrl: './progress-circle.template.html',
                    styleUrls: ['./progress-circle.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [MODE_PROVIDER],
                    host: {
                        '($.data-mode.attr)': 'mode$',
                    },
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [USER_AGENT]
                }] }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [ElementRef]
                }] }, { type: i1.Observable, decorators: [{
                    type: Inject,
                    args: [TUI_MODE]
                }] }]; }, propDecorators: { progressCircle: [{
                type: ViewChild,
                args: ['progressCircle', { static: true }]
            }], value: [{
                type: Input
            }], max: [{
                type: Input
            }], color: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['style.--tui-progress-color']
            }], size: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.data-size']
            }], progressRatio: [{
                type: HostBinding,
                args: ['style.--progress-ratio']
            }] } });

class TuiProgressLabelComponent {
}
TuiProgressLabelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TuiProgressLabelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiProgressLabelComponent, selector: "label[tuiProgressLabel]", ngImport: i0, template: "<ng-content select=\"progress\"></ng-content>\n<ng-content select=\"tui-progress-circle\"></ng-content>\n<span class=\"t-label\">\n    <ng-content></ng-content>\n</span>\n", styles: [":host{position:relative;display:inline-block;color:var(--tui-text-01)}.t-label{position:absolute;top:0;left:0;width:100%;height:100%;display:flex;font:var(--tui-font-text-s);flex-direction:column;justify-content:center;align-items:center}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'label[tuiProgressLabel]',
                    templateUrl: './progress-label.template.html',
                    styleUrls: ['./progress-label.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });

/**
 * @deprecated Use {@link http://taiga-ui.dev/experimental/progress-segmented TuiProgressSegmentedModule} (from `@taiga-ui/experimental`)
 * TODO: delete in v4.0
 */
class TuiProgressSegmentedComponent {
    constructor(mode$) {
        this.mode$ = mode$;
        this.value = 0;
        this.max = 1;
        this.size = 'm';
        this.colors = 'currentColor';
    }
    getActiveColor(index = 0) {
        return tuiIsString(this.colors)
            ? this.colors
            : this.colors[index] || this.colors[this.colors.length - 1];
    }
}
TuiProgressSegmentedComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressSegmentedComponent, deps: [{ token: TUI_MODE }], target: i0.ɵɵFactoryTarget.Component });
TuiProgressSegmentedComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiProgressSegmentedComponent, selector: "tui-progress-segmented", inputs: { value: "value", max: "max", size: "size", colors: "colors" }, host: { listeners: { "$.data-mode.attr": "mode$" }, properties: { "attr.data-size": "this.size" } }, providers: [MODE_PROVIDER], ngImport: i0, template: "<progress\n    class=\"t-hidden-progress\"\n    [max]=\"max\"\n    [value]=\"value\"\n></progress>\n\n<span\n    *tuiRepeatTimes=\"let index of max\"\n    aria-hidden=\"true\"\n    class=\"t-segment\"\n    [style.background]=\"index < value ? getActiveColor(index) : null\"\n></span>\n", styles: [":host{display:flex;color:var(--tui-primary)}:host[data-mode=onDark]{--tui-clear: var(--tui-clear-inverse)}.t-segment{transition-property:background;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;flex:1 1 100%;border-radius:var(--tui-radius-xs);background:var(--tui-clear)}.t-segment:not(:last-child){margin-right:.25rem}:host[data-size=s] .t-segment{height:.25rem}:host[data-size=m] .t-segment{height:.5rem}.t-hidden-progress{position:absolute;clip:rect(1px,1px,1px,1px);-webkit-clip-path:inset(50%);clip-path:inset(50%);height:1px;width:1px;margin:-1px;overflow:hidden;padding:0}\n"], directives: [{ type: i1$2.TuiRepeatTimesDirective, selector: "[tuiRepeatTimes][tuiRepeatTimesOf]", inputs: ["tuiRepeatTimesOf"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressSegmentedComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-progress-segmented',
                    templateUrl: './progress-segmented.template.html',
                    styleUrls: ['./progress-segmented.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [MODE_PROVIDER],
                    host: {
                        '($.data-mode.attr)': 'mode$',
                    },
                }]
        }], ctorParameters: function () { return [{ type: i1.Observable, decorators: [{
                    type: Inject,
                    args: [TUI_MODE]
                }] }]; }, propDecorators: { value: [{
                type: Input
            }], max: [{
                type: Input
            }], size: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.data-size']
            }], colors: [{
                type: Input
            }] } });

class TuiProgressModule {
}
TuiProgressModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiProgressModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressModule, declarations: [TuiProgressBarComponent,
        TuiProgressColorSegmentsDirective,
        TuiProgressCircleComponent,
        TuiProgressLabelComponent,
        TuiProgressSegmentedComponent], imports: [CommonModule, TuiRepeatTimesModule], exports: [TuiProgressBarComponent,
        TuiProgressCircleComponent,
        TuiProgressColorSegmentsDirective,
        TuiProgressLabelComponent,
        TuiProgressSegmentedComponent] });
TuiProgressModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressModule, imports: [[CommonModule, TuiRepeatTimesModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiProgressModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TuiRepeatTimesModule],
                    declarations: [
                        TuiProgressBarComponent,
                        TuiProgressColorSegmentsDirective,
                        TuiProgressCircleComponent,
                        TuiProgressLabelComponent,
                        TuiProgressSegmentedComponent,
                    ],
                    exports: [
                        TuiProgressBarComponent,
                        TuiProgressCircleComponent,
                        TuiProgressColorSegmentsDirective,
                        TuiProgressLabelComponent,
                        TuiProgressSegmentedComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TuiProgressBarComponent, TuiProgressCircleComponent, TuiProgressColorSegmentsDirective, TuiProgressLabelComponent, TuiProgressModule, TuiProgressSegmentedComponent };
